/**
 * Unified authentication guard hook for b3os-web.
 *
 * Reads auth state directly from the SDK's Zustand store rather than calling
 * `useAuthentication()` — which triggers Thirdweb's `useAutoConnect` and
 * races with `BetterAuthProvider.restoreSession()`.
 *
 * The race condition:
 *   1. useAutoConnect finds no ecosystem wallet → sets isAuthenticating: false
 *   2. BetterAuthProvider is still restoring the session async
 *   3. Brief gap: isAuthenticating=false AND isAuthenticated=false
 *   4. Pages render <OSLoginLanding /> → visible flash
 *
 * This hook avoids the race by reading the store directly. The store starts
 * with isAuthenticating: true and BetterAuthProvider is the sole writer.
 *
 * Replaces: useWorkflowAuth (which called useAuthentication internally)
 */

import { useSyncExternalStore } from "react";
import { useAuthStore } from "@b3dotfun/sdk/global-account/react";
import { getMasqueradeToken, MASQUERADE_CHANGED_EVENT } from "@/lib/masquerade";

function getMasqueradeSnapshot(): boolean {
  return !!getMasqueradeToken();
}

function getServerSnapshot(): boolean {
  return false;
}

function subscribeMasquerade(callback: () => void): () => void {
  window.addEventListener(MASQUERADE_CHANGED_EVENT, callback);
  return () => {
    window.removeEventListener(MASQUERADE_CHANGED_EVENT, callback);
  };
}

interface UseAppAuthResult {
  /** Whether the user is authenticated (or dev bypass is active) */
  isAuthenticated: boolean;
  /** Whether authentication is currently in progress (session restore) */
  isAuthenticating: boolean;
  /** Whether authentication has completed (regardless of result) */
  isReady: boolean;
}

/**
 * Hook for checking authentication status without triggering Thirdweb auto-connect.
 *
 * Usage:
 * ```tsx
 * const { isAuthenticated, isAuthenticating, isReady } = useAppAuth();
 *
 * if (!isReady) return <AuthLoadingCard />;
 * if (!isAuthenticated) {
 *   // Redirect to login — never render login page inline
 *   window.location.href = buildLoginRedirectUrl(pathname);
 *   return <AuthLoadingCard />;
 * }
 * return <AuthenticatedContent />;
 * ```
 */
export function useAppAuth(): UseAppAuthResult {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const isAuthenticating = useAuthStore(state => state.isAuthenticating);
  const isMasquerading = useSyncExternalStore(subscribeMasquerade, getMasqueradeSnapshot, getServerSnapshot);

  const effectiveIsAuthenticated = process.env.NEXT_PUBLIC_DEV_AUTH_TOKEN ? true : isAuthenticated || isMasquerading;

  return {
    isAuthenticated: effectiveIsAuthenticated,
    isAuthenticating: isMasquerading ? false : isAuthenticating,
    isReady: isMasquerading ? true : !isAuthenticating,
  };
}
